import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { useInquiry } from "../context/InquiryContext";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { totalCount, openInquiry } = useInquiry();

  const navItems = [
    { name: "Forside", path: "/" },
    { name: "Produkter", path: "/produkter" },
    { name: "Ydelser", path: "/ydelser" },
    { name: "Priser", path: "/priser" },
    { name: "Om os", path: "/om-os" },
    { name: "Galleri", path: "/galleri" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-orange-600 border-b border-orange-700 shadow-sm text-white">
      <div className="container-custom">
        {/* Row 1: Logo & CTA Area */}
        <div className="flex justify-between items-center pt-1 pb-2 lg:pt-2 lg:pb-4">
          <Link to="/" className="flex items-center gap-2 group">
            <img 
              src="/6f4dcea8-90c6-4c14-95d1-820165e65591-removebg-preview.png" 
              alt="Hj 3Ddesign Logo" 
              className="h-20 md:h-24 lg:h-32 w-auto object-contain transition-transform group-hover:scale-105 brightness-0 invert" 
            />
          </Link>

          <div className="flex items-center gap-3 md:gap-4">
            {totalCount > 0 && (
              <button
                type="button"
                onClick={() => openInquiry()}
                className="inline-flex items-center gap-2 px-3.5 py-2.5 bg-slate-900 text-white text-xs font-bold uppercase tracking-wider hover:bg-slate-800 transition-all cursor-pointer shadow-md"
                aria-label={`Åbn forespørgsel med ${totalCount} varer`}
              >
                <span>Forespørgsel</span>
                <span className="bg-orange-500 text-white text-[11px] font-bold px-1.5 py-0.2 rounded-full">
                  {totalCount}
                </span>
              </button>
            )}

            <Link
              to="/kontakt"
              className="hidden md:inline-flex items-center justify-center px-8 py-3 rounded-none bg-white text-orange-600 text-[11px] font-bold uppercase tracking-widest hover:bg-orange-50 transition-all hover:scale-105 active:scale-95 shadow-xl shadow-orange-900/20"
            >
              Kontakt os
            </Link>

            {/* Mobile Toggle */}
            <button
              className="md:hidden p-2 text-white hover:text-orange-100 transition-colors z-50"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>
        </div>

        {/* Row 2: Desktop Nav (Left) */}
        <div className="hidden md:flex items-center pt-1 pb-6 lg:pb-8">
          <nav className="flex items-center gap-6 lg:gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-[11px] lg:text-xs font-bold tracking-[0.2em] uppercase transition-colors relative pb-1 ${
                  isActive(item.path) 
                    ? "text-white after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-white" 
                    : "text-orange-100 hover:text-white"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Nav Sidebar */}
      <div 
        className={`fixed inset-0 z-[60] md:hidden transition-all duration-500 ease-in-out ${
          isOpen ? "visible" : "invisible pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-500 ${
            isOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setIsOpen(false)}
        />
        
        {/* Sidebar Content */}
        <div 
          className={`absolute top-0 right-0 h-full w-[80%] max-w-sm bg-white shadow-2xl transition-transform duration-500 ease-out flex flex-col ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Sidebar Header */}
          <div className="p-6 border-b border-slate-50 flex flex-col items-center relative bg-orange-600">
            <button 
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition-colors"
              aria-label="Close menu"
            >
              <X size={28} />
            </button>
            
            <div 
              className={`transition-all duration-700 delay-200 ${
                isOpen ? "opacity-100 scale-100" : "opacity-0 scale-90"
              }`}
            >
              <img 
                src="/6f4dcea8-90c6-4c14-95d1-820165e65591-removebg-preview.png" 
                alt="Hj 3Ddesign Logo" 
                className="h-36 w-auto object-contain my-4 brightness-0 invert" 
              />
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 overflow-y-auto py-8 px-8">
            <div className="flex flex-col gap-6">
              {[...navItems, { name: "Kontakt", path: "/kontakt" }].map((item, index) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`group flex items-center justify-between text-lg font-bold tracking-tight transition-all duration-300 ${
                    isActive(item.path) ? "text-orange-600" : "text-slate-500 hover:text-slate-900"
                  }`}
                  style={{ 
                    transitionDelay: isOpen ? `${index * 50}ms` : "0ms",
                    transform: isOpen ? "translateX(0)" : "translateX(20px)",
                    opacity: isOpen ? 1 : 0
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  <span>{item.name}</span>
                  <div className={`h-px flex-1 mx-4 bg-slate-100 transition-all duration-500 ${
                    isActive(item.path) ? "bg-orange-600 w-full" : "w-0 group-hover:w-full group-hover:bg-slate-200"
                  }`} />
                </Link>
              ))}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className="p-8 border-t border-slate-50 bg-slate-50/50">
            <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-4">
              Har du spørgsmål?
            </p>
            <Link
              to="/kontakt"
              onClick={() => setIsOpen(false)}
              className="block w-full py-4 bg-orange-600 text-white text-center text-xs font-bold uppercase tracking-widest hover:bg-orange-700 transition-colors"
            >
              Skriv til os
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
